import isString from 'lodash/isString';
import { useDropzone } from 'react-dropzone';
// @mui
import { styled } from '@mui/material/styles';
import { Box, IconButton } from '@mui/material';

// type
//
import ImageLegacy from '../image/imageLegacy';
import RejectionFiles from './RejectionFiles';
import BlockContent from './BlockContent';
import { UploadProps } from './types';
import CloseIcon from '@mui/icons-material/Close';
import { useState } from 'react';

// ----------------------------------------------------------------------

const DropZoneStyle = styled('div')(({ theme }) => ({
  outline: 'none',
  overflow: 'hidden',
  position: 'relative',
  padding: theme.spacing(5, 1),
  borderRadius: theme.shape.borderRadius,
  transition: theme.transitions.create('padding'),
  backgroundColor: theme.palette.background.neutral,
  border: `1px dashed ${theme.palette.grey[500]}`,
  '&:hover': { opacity: 0.72, cursor: 'pointer' },
}));

// ----------------------------------------------------------------------

export default function UploadSingleFileAccelerator({
  error = false,
  showPreview = false,
  file,
  helperText,
  sx,
  iconImage,
  carouselImage,
  onRemove,
  ...other
}: UploadProps) {
  const [hovered, setHovered] = useState(false);
  const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
    multiple: false,
    maxSize: 600000,
    ...other,
  });

  return (
    <Box
      sx={{ width: '100%', ...sx, position: 'relative' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <DropZoneStyle
        {...getRootProps()}
        sx={{
          ...(isDragActive && { opacity: 0.72 }),
          ...((isDragReject || error) && {
            color: 'error.main',
            borderColor: 'error.light',
            bgcolor: 'error.lighter',
          }),
          // eslint-disable-next-line no-nested-ternary
          ...(iconImage
            ? {
                padding: 0,
                width: 165,
                height: 165,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }
            : file
              ? {
                  padding: '12% 0',
                }
              : {}),
          // eslint-disable-next-line no-nested-ternary
          ...(carouselImage
            ? {
                padding: 0,
                width: 180,
                height: 180,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }
            : file
              ? {
                  padding: '12% 0',
                }
              : {}),
        }}
      >
        <input {...getInputProps()} />

        {!file && <BlockContent />}

        {file && (
          <>
            <ImageLegacy
              alt="file preview"
              src={isString(file) ? file : file.preview}
              sx={{
                ...(iconImage || carouselImage
                  ? {
                      borderRadius: iconImage ? 2 : 0,
                      width: 180,
                      height: 180,
                      objectFit: 'cover',
                      position: 'relative',
                    }
                  : {
                      top: 8,
                      left: 8,
                      borderRadius: 1,
                      position: 'absolute',
                      width: 'calc(100% - 16px)',
                      height: 'calc(100% - 16px)',
                    }),
              }}
            />
            {onRemove && hovered && (
              <IconButton
                size="small"
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'rgba(255,255,255,0.7)',
                  zIndex: 2,
                  '&:hover': { bgcolor: 'error.main', color: 'white' },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(file);
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </>
        )}
      </DropZoneStyle>

      {/* @ts-ignore */}
      {fileRejections.length > 0 && <RejectionFiles fileRejections={fileRejections} />}
      {helperText && helperText}
    </Box>
  );
}
